import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ItemCreationSection } from '~/Components';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { isObjectEmpty } from '~/Helpers/CleanData.helper';
import { useProvider } from '~/Hooks';
import { Provider } from '~/Models/DTO/Provider.model';
import { selectErrors } from '~/store/app';
import { requestUpdateProvider } from '~/store/provider';
import { ProviderSettingsGeneralSection } from './ProviderSettingsSections';
import { ProviderSettingsValidation } from './ProviderSettingsValidation';
import { settingsDetailVerification } from './verification';

const _initialValues: Partial<Provider> = {
  uuid: '',
  name: '',
};

export const useProviderSettings = () => {
  // store
  const dispatch = useDispatch();
  const providerUpdateError = useSelector(selectErrors(REQUEST_ACTIONS.UPDATE_PROVIDER));
  const updateProvider = bindActionCreators(requestUpdateProvider, dispatch);

  // hooks
  const { t } = useTranslation();
  const { currentProvider: provider } = useProvider();

  // state
  const [currentProvider, setCurrentProvider] = useState(_initialValues);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // compute
  const sections: ItemCreationSection<Provider>[] = useMemo(
    () => [({ data }) => ProviderSettingsGeneralSection(data)],
    [],
  );

  const onSave = useCallback(async () => {
    const updatedValues = Object.entries(currentProvider)
      .filter((entry) => provider?.[entry[0] as keyof Provider] !== entry[1])
      .reduce((acc, entry) => ({ ...acc, [entry[0] as keyof Provider]: entry[1] } as Provider), {});
    if (!isObjectEmpty(updatedValues)) {
      const providerId = currentProvider?.uuid || '';
      setIsSubmitDisabled(true);
      updateProvider(providerId, {
        provider: updatedValues,
      });
    }
  }, [currentProvider]);

  const validate = useCallback((validateProvider: any) => {
    const errorsDetail = settingsDetailVerification(validateProvider);
    setIsSubmitDisabled(!isObjectEmpty(errorsDetail));
    setCurrentProvider(validateProvider);
  }, []);

  return {
    listItems: sections,
    onSave,
    initialValues: provider || _initialValues,
    validate,
    currentProvider,
    error: providerUpdateError || {},
    isLoading: false,
    isSubmitDisabled: isSubmitDisabled || hasValidationErrors,
    validationSchema: ProviderSettingsValidation(t),
    onValidationErrors: setHasValidationErrors,
  };
};
